import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

// Response header carrying a fresh access token on every authenticated
// request (set by the backend get_current_user dependency). Keep in sync with
// backend/app/auth/dependencies.ts::NEW_ACCESS_TOKEN_HEADER.
const NEW_ACCESS_TOKEN_HEADER = 'x-new-access-token';

export const apiClient = axios.create({
  baseURL: API_BASE,
});

// Attach JWT token to every request
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Sliding session + refresh-and-retry on 401 ---------------------------
//
// Sliding session: on every successful response, if the backend handed back a
// fresh access token via the X-New-Access-Token header, store it so the
// session stays alive as long as the user is active (true 30-min inactivity
// timeout rather than a hard 30-min wall-clock cutoff).
//
// Refresh-and-retry: on a 401, before clearing tokens + redirecting to login,
// attempt a single /auth/refresh call with the stored refresh token. If it
// succeeds, rotate both stored tokens and retry the original request once. A
// module-level `isRefreshing` flag + a queue of waiting requests ensures that
// many concurrent 401s share ONE refresh call rather than firing N refreshes
// (which would race and rotate the refresh token under each other).

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let waitQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function clearTokensAndRedirect() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

function doRefresh(refreshToken: string): Promise<string> {
  // Single-flight: if a refresh is already in flight, piggyback on it.
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      const { data } = await axios.post(`${API_BASE}/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const newAccess: string = data.access_token;
      const newRefresh: string | undefined = data.refresh_token;
      localStorage.setItem('access_token', newAccess);
      if (newRefresh) {
        localStorage.setItem('refresh_token', newRefresh);
      }
      // Release any queued requests with the new access token.
      const queue = waitQueue;
      waitQueue = [];
      for (const w of queue) {
        w.resolve(newAccess);
      }
      return newAccess;
    } catch (err) {
      // Refresh failed -> flush the queue with the error so callers reject.
      const queue = waitQueue;
      waitQueue = [];
      for (const w of queue) {
        w.reject(err);
      }
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// Sliding session: capture fresh access token from the response header.
apiClient.interceptors.response.use((response: AxiosResponse) => {
  const newToken = response.headers?.[NEW_ACCESS_TOKEN_HEADER] as
    | string
    | undefined;
  if (newToken) {
    localStorage.setItem('access_token', newToken);
  }
  return response;
});

// 401 handling: refresh-and-retry once, else clear + redirect.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    const original = error.config as RetryableConfig | undefined;
    const url = original?.url ?? '';
    // Never recurse into refresh on the /auth/refresh call itself, and only
    // retry once per request.
    if (!original || url.includes('/auth/refresh') || original._retried) {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }
    const storedRefresh = localStorage.getItem('refresh_token');
    if (!storedRefresh) {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      let newAccess: string;
      if (isRefreshing) {
        // Queue this request behind the in-flight refresh.
        newAccess = await new Promise<string>((resolve, reject) => {
          waitQueue.push({ resolve, reject });
        });
      } else {
        isRefreshing = true;
        newAccess = await doRefresh(storedRefresh);
      }
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization =
        `Bearer ${newAccess}`;
      return apiClient(original as AxiosRequestConfig);
    } catch {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }
  }
);

export default apiClient;